import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { categories } from '../data/categories';
import { products } from '../data/products';
import { priceRanges, sortOptions, type PriceRange, type SortKey } from '../data/shopFilters';
import { getCreator, isNewProduct, isProductLeavingSoon } from '../utils/catalog';
import type { CategoryId, Product } from '../types/catalog';

export interface ActiveFilter {
  key: string;
  label: string;
  onRemove: () => void;
}

export function useShopFilters() {
  const [params, setParams] = useSearchParams();

  const categoryParam = params.get('categorie');
  const category = categories.some((c) => c.id === categoryParam) ? (categoryParam as CategoryId) : null;
  const creatorIds = params.getAll('createur');
  const price = (priceRanges.some((p) => p.value === params.get('prix')) ? params.get('prix') : 'all') as PriceRange;
  const sort = (sortOptions.some((s) => s.value === params.get('tri')) ? params.get('tri') : 'featured') as SortKey;
  const leavingOnly = params.get('derniers') === '1';
  const uniqueOnly = params.get('unique') === '1';

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(params);
      mutate(next);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const setCategory = (id: CategoryId | null) =>
    update((n) => (id ? n.set('categorie', id) : n.delete('categorie')));
  const toggleCreator = (id: string) =>
    update((n) => {
      const current = n.getAll('createur');
      n.delete('createur');
      const next = current.includes(id) ? current.filter((c) => c !== id) : [...current, id];
      next.forEach((c) => n.append('createur', c));
    });
  const setPrice = (value: PriceRange) => update((n) => (value === 'all' ? n.delete('prix') : n.set('prix', value)));
  const setSort = (value: SortKey) => update((n) => (value === 'featured' ? n.delete('tri') : n.set('tri', value)));
  const toggleLeaving = () => update((n) => (leavingOnly ? n.delete('derniers') : n.set('derniers', '1')));
  const toggleUnique = () => update((n) => (uniqueOnly ? n.delete('unique') : n.set('unique', '1')));
  const resetFilters = () =>
    update((n) => {
      ['createur', 'prix', 'derniers', 'unique'].forEach((k) => n.delete(k));
    });

  const range = priceRanges.find((p) => p.value === price) ?? priceRanges[0];

  const baseFiltered = useMemo(
    () =>
      products.filter((p) => {
        if (creatorIds.length && !creatorIds.includes(p.creatorId)) return false;
        if (p.price < range.min || p.price >= range.max) return false;
        if (leavingOnly && !isProductLeavingSoon(p)) return false;
        if (uniqueOnly && p.stock !== 1) return false;
        return true;
      }),
    [creatorIds.join(','), range, leavingOnly, uniqueOnly],
  );

  const results = useMemo(() => {
    const list = category ? baseFiltered.filter((p) => p.category === category) : [...baseFiltered];
    return sortProducts(list, sort);
  }, [baseFiltered, category, sort]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: baseFiltered.length };
    categories.forEach((c) => {
      counts[c.id] = baseFiltered.filter((p) => p.category === c.id).length;
    });
    return counts;
  }, [baseFiltered]);

  const activeFilters: ActiveFilter[] = [
    ...(leavingOnly ? [{ key: 'derniers', label: 'Derniers jours', onRemove: toggleLeaving }] : []),
    ...(uniqueOnly ? [{ key: 'unique', label: 'Pièces uniques', onRemove: toggleUnique }] : []),
    ...(price !== 'all' ? [{ key: 'prix', label: range.label, onRemove: () => setPrice('all') }] : []),
    ...creatorIds.map((id) => ({
      key: `c-${id}`,
      label: getCreator(id)?.name ?? id,
      onRemove: () => toggleCreator(id),
    })),
  ];

  return {
    category,
    creatorIds,
    price,
    sort,
    leavingOnly,
    uniqueOnly,
    results,
    categoryCounts,
    activeFilters,
    setCategory,
    toggleCreator,
    setPrice,
    setSort,
    toggleLeaving,
    toggleUnique,
    resetFilters,
  };
}

export type ShopFiltersState = ReturnType<typeof useShopFilters>;

function sortProducts(list: Product[], sort: SortKey): Product[] {
  switch (sort) {
    case 'new':
      return list.sort((a, b) => Number(isNewProduct(b)) - Number(isNewProduct(a)) || b.addedAt.localeCompare(a.addedAt));
    case 'leaving':
      return list.sort((a, b) =>
        (getCreator(a.creatorId)?.exhibitionEnd ?? '').localeCompare(getCreator(b.creatorId)?.exhibitionEnd ?? ''),
      );
    case 'price-asc':
      return list.sort((a, b) => a.price - b.price);
    case 'price-desc':
      return list.sort((a, b) => b.price - a.price);
    default:
      return list;
  }
}
